import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const EMPTY_FORM = {
  employee_job_role_id: '',
  name: '',
  description: '',
  is_active: true,
  sort_order: 0
};

export default function EmployeeOfferedTitles() {
  const { hasPermission } = useAuth();
  const canModify = hasPermission('users_modify');
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadRoles = useCallback(async () => {
    try {
      const r = await api.get('/contacts/employee-job-roles');
      setRoles(r.data?.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load job roles');
      setRoles([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/employee-offered-titles');
      setRows(r.data?.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load employee offered titles');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      employee_job_role_id: row.employee_job_role_id || '',
      name: row.name || '',
      description: row.description || '',
      is_active: row.is_active !== false,
      sort_order: Number(row.sort_order || 0)
    });
    setOpen(true);
  };

  const save = async () => {
    if (!String(form.employee_job_role_id || '').trim()) {
      toast.error('Employee Job Role is required');
      return;
    }
    if (!String(form.name || '').trim()) {
      toast.error('Offered title name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        employee_job_role_id: form.employee_job_role_id,
        name: String(form.name).trim(),
        description: form.description != null ? String(form.description) : null,
        is_active: form.is_active,
        sort_order: Number(form.sort_order || 0)
      };
      if (editing?.id) {
        await api.put(`/admin/employee-offered-titles/${editing.id}`, payload);
        toast.success('Employee offered title updated');
      } else {
        await api.post('/admin/employee-offered-titles', payload);
        toast.success('Employee offered title created');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save employee offered title');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await api.delete(`/admin/employee-offered-titles/${row.id}`);
      toast.success('Employee offered title deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete employee offered title');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Employee Offered Title
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define negotiated / offer-letter titles per Employee Job Role. Employee contacts choose one after selecting a
            role; this is what they see when they log in (separate from the internal Employee Level catalog).
          </Typography>
        </Box>
        {canModify && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
            Add Employee Offered Title
          </Button>
        )}
      </Box>

      {loading ? (
        <LinearProgress />
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Job Role</TableCell>
                  <TableCell>Offered Title</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Sort</TableCell>
                  {canModify && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canModify ? 6 : 5}>
                      <Typography color="text.secondary">No employee offered titles found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Typography fontWeight={600}>
                          {row.employeeJobRole?.name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.description || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.is_active ? 'Active' : 'Inactive'}
                          color={row.is_active ? 'success' : 'default'}
                          variant={row.is_active ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">{Number(row.sort_order || 0)}</TableCell>
                      {canModify && (
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => remove(row)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Employee Offered Title' : 'Add Employee Offered Title'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Employee Job Role</InputLabel>
            <Select
              label="Employee Job Role"
              value={form.employee_job_role_id || ''}
              onChange={(e) => setForm((prev) => ({ ...prev, employee_job_role_id: e.target.value }))}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Offered title"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            multiline
            minRows={2}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Sort Order"
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm((prev) => ({ ...prev, sort_order: Number(e.target.value || 0) }))}
          />
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Active
            </Typography>
            <Switch
              checked={form.is_active}
              onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
